import { ref, computed } from 'vue'
import onboardingService from '@/services/onboardingService'
import { EDITABLE_STATUSES, ONBOARDING_SECTION, AUTOSAVE_DELAY_MS } from '@/constants/onboarding'

/**
 * The supplier's registration draft.
 *
 * Module-level state, not per-component: the shell, the navigator and whichever step is
 * on screen all read the same draft, and fetching once keeps them from disagreeing or
 * issuing three identical requests on every step change.
 */
const draft = ref(null)
const loading = ref(false)
const loaded = ref(false)
const saving = ref(false)
const error = ref(null)
const lastSavedAt = ref(null)

/**
 * Payloads waiting for the autosave timer, keyed by section. A map rather than a single
 * value because a supplier can move to another step inside one debounce window; keeping
 * only the last payload would drop the section they just left.
 *
 * A plain Map, so its size is invisible to Vue — `pendingCount` mirrors it for anything
 * that needs to react. Keep the two in step whenever the map is touched.
 */
const pending = new Map()
const pendingCount = ref(0)
let autosaveTimer = null

/** The draft fetch in progress, shared by every caller that asks while it runs. */
let inFlight = null
/**
 * Bumped by every fetch and by reset(), so only the latest fetch writes the draft: an
 * older one cannot overwrite a forced reload, nor leak into the session after a logout.
 */
let loadGeneration = 0

/** Section → endpoint. Adding a section without adding it here would fail silently. */
const SECTION_SAVERS = {
  [ONBOARDING_SECTION.COMPANY_IDENTITY]: data => onboardingService.updateCompanyIdentity(data),
  [ONBOARDING_SECTION.ADDRESSES]: data => onboardingService.updateAddresses(data)
}

export function useOnboardingDraft() {
  const load = async (force = false) => {
    // A caller arriving while a fetch is in flight waits for it. Returning the current
    // (still empty) draft instead told the router guard and the sidebar, which both ask
    // on a fresh page load, that there was no registration — so the full menu flashed
    // in and a direct link to any page opened for an unapproved supplier.
    //
    // A forced reload is never folded into an earlier fetch: it runs after a submit,
    // when the status has just changed and the fetch in flight may predate that.
    if (inFlight && !force) return inFlight
    if (loaded.value && !force) return draft.value

    const generation = ++loadGeneration
    loading.value = true
    error.value = null
    const request = (async () => {
      try {
        const result = await onboardingService.getDraft()
        // Superseded by a newer fetch, or reset() ran meanwhile (logout, tenant switch):
        // this answer is stale or for someone else.
        if (generation !== loadGeneration) return draft.value
        draft.value = result
        loaded.value = true
      } catch (err) {
        if (generation !== loadGeneration) return draft.value
        // Unlike profile completion, a missing draft is not cosmetic — there is nothing
        // to show without it — so the error is surfaced rather than swallowed.
        error.value = err
        draft.value = null
      } finally {
        if (inFlight === request) {
          inFlight = null
          loading.value = false
        }
      }
      return draft.value
    })()
    inFlight = request
    return request
  }

  /**
   * Write one section now and adopt the draft the server returns.
   *
   * The response replaces local state wholesale because the server owns `completedSteps`
   * and `status`; recomputing either on the client would let the two drift apart.
   *
   * @param {string} section one of ONBOARDING_SECTION
   * @param {Object} payload
   * @returns {Promise<boolean>} whether the write landed
   */
  const saveSection = async (section, payload) => {
    const saver = SECTION_SAVERS[section]
    if (!saver) {
      error.value = new Error(`Unknown onboarding section: ${section}`)
      return false
    }

    saving.value = true
    error.value = null
    try {
      draft.value = await saver(payload)
      lastSavedAt.value = new Date()
      return true
    } catch (err) {
      error.value = err
      return false
    } finally {
      saving.value = false
    }
  }

  /**
   * Hold a section's latest values and write them once typing pauses.
   *
   * The spec asks for a save after every step; debouncing turns that into something a
   * supplier can actually live with while filling a long form.
   */
  const queueSave = (section, payload) => {
    pending.set(section, payload)
    pendingCount.value = pending.size
    if (autosaveTimer) clearTimeout(autosaveTimer)
    autosaveTimer = setTimeout(() => { flush() }, AUTOSAVE_DELAY_MS)
  }

  /**
   * Write everything queued, right now.
   *
   * Call before leaving a step or unloading the page — otherwise the last edits sit in
   * the timer and are lost, which is the one thing a resumable form must never do.
   *
   * @returns {Promise<boolean>} whether every queued write landed
   */
  const flush = async () => {
    if (autosaveTimer) {
      clearTimeout(autosaveTimer)
      autosaveTimer = null
    }
    if (pending.size === 0) return true

    // Drained first so a write that fails does not get retried forever by a later flush,
    // and so edits made during the await are not silently discarded.
    const entries = [...pending.entries()]
    pending.clear()
    pendingCount.value = 0

    let allSaved = true
    for (const [section, payload] of entries) {
      // Sequential on purpose: each endpoint returns the whole draft, and firing them in
      // parallel would leave whichever resolved last as the winner.
      const saved = await saveSection(section, payload)
      if (!saved) allSaved = false
    }
    return allSaved
  }

  /** True while a section has unwritten edits. Drives the unload warning. */
  const hasPendingChanges = computed(() => pendingCount.value > 0)

  const status = computed(() => draft.value?.status ?? null)

  /** After submission the draft is what an admin is reading, so the forms lock. */
  const isEditable = computed(() => EDITABLE_STATUSES.includes(status.value))

  const completedSteps = computed(() => draft.value?.completedSteps ?? [])

  /**
   * Whether this tenant has a registration at all.
   *
   * False for tenants that joined before the flow existed. They have no draft row, so
   * enforcement must leave them alone rather than lock them out of a process they never
   * began.
   */
  const isRegistrationStarted = computed(() => draft.value?.isRegistrationStarted === true)

  /** Clear everything — on logout, or when impersonation switches tenant. */
  const reset = () => {
    if (autosaveTimer) {
      clearTimeout(autosaveTimer)
      autosaveTimer = null
    }
    pending.clear()
    pendingCount.value = 0
    inFlight = null
    loadGeneration++
    draft.value = null
    loaded.value = false
    loading.value = false
    saving.value = false
    error.value = null
    lastSavedAt.value = null
  }

  return {
    draft,
    loading,
    loaded,
    saving,
    error,
    lastSavedAt,
    status,
    isEditable,
    completedSteps,
    isRegistrationStarted,
    hasPendingChanges,
    load,
    refresh: () => load(true),
    saveSection,
    queueSave,
    flush,
    reset
  }
}
